<script setup lang="ts" generic="T extends unknown">
import { isUndefined } from "lodash-es";
import { ref, watch } from "vue";

type SelectedItem = {
  index: number;
  item: T;
};

const props = withDefaults(
  defineProps<{
    selected?: number;
    items: T[];
  }>(),
  { selected: 0 }
);

const emit = defineEmits<{
  (event: "clickSelected"): void;
  (event: "input", value: SelectedItem): void;
}>();

const CLASS_NAME_COMPONENT_BUTTON_GROUP = "le-selector-button-group";

const selectedIndex = ref<number | undefined>();
const tabbableIndex = ref<number | undefined>();

watch(
  () => props.selected,
  (newIndex: number) => {
    selectedIndex.value = undefined;
    tabbableIndex.value = 0;

    if (newIndex === -1) {
      return;
    }

    if (isUndefined(props.items[newIndex])) {
      return;
    }

    selectedIndex.value = newIndex;
    tabbableIndex.value = newIndex;
  },
  {
    immediate: true
  }
);

function handleBlur() {
  if (isUndefined(selectedIndex.value)) {
    tabbableIndex.value = 0;

    return;
  }

  tabbableIndex.value = selectedIndex.value;
}

function handleSelect(activeIndex: number) {
  tabbableIndex.value = activeIndex;

  if (selectedIndex.value === activeIndex) {
    emit("clickSelected");

    return;
  }

  selectedIndex.value = activeIndex;
  emit("input", {
    index: activeIndex,
    item: props.items[activeIndex]
  });
}

function handleFocusNext(activeIndex: number) {
  tabbableIndex.value = activeIndex + 1 === props.items.length ? 0 : activeIndex + 1;
  focusTabbableIndexElement();
}

function handleFocusPrev(activeIndex: number) {
  tabbableIndex.value = activeIndex === 0 ? props.items.length - 1 : activeIndex - 1;
  focusTabbableIndexElement();
}

function focusTabbableIndexElement() {
  if (tabbableIndex.value === undefined) {
    return;
  }
  const el = document?.activeElement?.parentElement?.children?.[tabbableIndex.value] as HTMLElement;

  if (el) {
    el.focus();
  }
}
</script>

<template>
  <div role="radiogroup" :class="CLASS_NAME_COMPONENT_BUTTON_GROUP">
    <slot
      :handle-blur="handleBlur"
      :handle-select="handleSelect"
      :handle-focus-next="handleFocusNext"
      :handle-focus-prev="handleFocusPrev"
      :state="{ selectedIndex, tabbableIndex }"
    />

    <!-- Adds possibility to have elements inline with the default slot / SelectorButtons -->
    <slot name="after" />
  </div>
</template>
